import type { DocumentMapping } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";

// Phase 2 added the classifications that resolve to a single, unconditional behavior — one
// folder, and either always a task or never a task.
//
// Phase 3 adds the "within N months -> task; older -> no task" classifications, now that
// document-management-instructions.ts (getAcsDocumentManagementInstructions) provides the
// freshness-gate mechanism, plus LAB_RESULTS_PATHOLOGY / PROCEDURE_SLEEP / PROGRESS_NOTE_
// HOSPITAL_RECORDS, which were held back in phase 2 pending an answer for "owning Nurse
// Practitioner" as assignee. That's a per-patient dynamic value (varies by which NP owns the
// chart) — we don't yet have either (a) a physician->NP roster from ACS, or (b) confirmation that
// NextGen's patient PCP field already resolves directly to the owning NP (see
// nextgen.ts:assignedToPractitionerEhrId, which already reads patientDemographics.
// primaryCareProviderId for an unrelated feature — worth asking ACS/NextGen IT whether that same
// field is usable here). Until one of those is confirmed, "Owning Nurse Practitioner" is shown as
// a literal role string, mirroring ewei-torrance's own "Physician" role-string placeholder for the
// same kind of unresolved per-provider assignment — not a specific (and potentially wrong) person.
//
// Still explicitly NOT included (see the onboarding PR description for the full breakdown):
//   - LAB_RESULTS_MULTIPLE_LABS: freshness-gated AND has a compound "add a second INR task in
//     addition to the standard lab task" requirement — more than this mechanism covers.
//   - PROGRESS_NOTE: freshness-gated AND the folder itself varies dynamically by `specialtyFolder`
//     (a per-value folder lookup we don't have placeholder IDs for yet).
//   - Anything needing a dynamic assignee resolved from an extracted field (COVER_PAGE,
//     ORDER_CLARIFICATION, MEDICAL_RECORD_REQUEST_RESPONSE's attentionName branch): needs an
//     assignee-resolver, not a mapping entry.
//   - Anything needing a chart lookup (CLEARANCE_REQUEST's lastOfficeVisitDate/patientLocation,
//     PRIOR_AUTHORIZATION_REFERRAL_RESPONSE): not an extraction field, an EHR API call.
//   - CARE_PLAN, NOTIFICATION, MEDICAL_RECORD_REQUEST(+HEDIS), REFERRAL_STATUS_REQUEST: each has
//     its own net-new field or reclassification/email-branch design question — see PR description.
//   - The IMAGING_RESULTS_CT/ECHO/ENDOSCOPY/ULTRASOUND folder-split question is still open.
//
// All "task" placeholders below (workgroup/staff IDs) are unverified pending ACS/NextGen IT
// confirmation, same as Shelby Brown's assignee ID from phase 1.
export const ACS_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Referral from {{ referringProvider }} received {{ dateReceived }}. Please schedule.",
      "Authorization Status: {{ authorizationStatus }}",
    ],
    // PLACEHOLDER — swap for Shelby Brown's real assignee identifier before launch.
    assignedTo: "PLACEHOLDER_SHELBY_BROWN_ASSIGNEE_ID",
    displayName: "Referral",
    documentLabel: "{{ dateReceived }} Referral - {{ patientInitials }}",
    // PLACEHOLDER — folder name taken from the onsite mapping sheet, unverified against ACS's
    // live NextGen document-type list.
    folder: "Referrals",
    // PLACEHOLDER — real NextGen document-type UUID; must match the key used in
    // getAcsNextGenDocumentTypeMetadata (src/lib/ehr/nextgen/nextgen-config.ts).
    folderMapping: "PLACEHOLDER_ACS_REFERRAL_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    priority: "Normal",
    classification: Classification.REFERRAL,
    group: ConfigSubdomain.ACS,
  },
  [Classification.REFERRAL_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Referral request from {{ referringProvider }} received {{ dateReceived }}. Please schedule.",
    ],
    assignedTo: "PLACEHOLDER_SHELBY_BROWN_ASSIGNEE_ID",
    displayName: "Referral Request",
    documentLabel: "{{ dateReceived }} Referral Request - {{ patientInitials }}",
    folder: "Referrals",
    folderMapping: "PLACEHOLDER_ACS_REFERRAL_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    priority: "Normal",
    classification: Classification.REFERRAL_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.REFERRAL_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Referral Response",
    documentLabel: "{{ dateReceived }} Referral Response - {{ patientInitials }}",
    folder: "Referrals",
    folderMapping: "PLACEHOLDER_ACS_REFERRAL_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.REFERRAL_RESPONSE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Medication prior authorization request received {{ dateReceived }}. Please review in Document Management.",
    ],
    // PLACEHOLDER — Pharmacy workgroup identifier, unverified against ACS's live NextGen setup.
    assignedTo: "PLACEHOLDER_ACS_PHARMACY_WORKGROUP_ID",
    displayName: "Prior Authorization Medication Request",
    documentLabel: "{{ dateReceived }} Med PA Request - {{ patientInitials }}",
    folder: "Pharmacy",
    folderMapping: "PLACEHOLDER_ACS_PHARMACY_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Medication prior authorization {{ authorizationStatus }} received {{ dateReceived }}. Please review in Document Management — denials are wanted, do not discard.",
    ],
    assignedTo: "PLACEHOLDER_ACS_PHARMACY_WORKGROUP_ID",
    displayName: "Prior Authorization Medication Response",
    documentLabel: "{{ dateReceived }} Med PA Response - {{ patientInitials }}",
    folder: "Pharmacy",
    folderMapping: "PLACEHOLDER_ACS_PHARMACY_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS_PT_INR]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: PT/INR result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    // PLACEHOLDER — Coumadin workgroup identifier, unverified against ACS's live NextGen setup.
    // Always tasked to Coumadin, never the owning NP — collectionDate does not gate this task.
    assignedTo: "PLACEHOLDER_ACS_COUMADIN_WORKGROUP_ID",
    displayName: "PT/INR",
    documentLabel: "{{ collectionDate }} PT-INR - {{ patientInitials }}",
    folder: "INR",
    folderMapping: "PLACEHOLDER_ACS_INR_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_PT_INR,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS_TEST_NOT_PERFORMED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Lab Results - Test Not Performed",
    documentLabel: "{{ dateReceived }} Lab - Test Not Performed - {{ patientInitials }}",
    folder: "Labs",
    folderMapping: "PLACEHOLDER_ACS_LABS_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_TEST_NOT_PERFORMED,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS_BP]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "BP Log",
    documentLabel: "{{ collectionDate }} BP Log - {{ patientInitials }}",
    folder: "Labs",
    folderMapping: "PLACEHOLDER_ACS_LABS_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_BP,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PHARMACY_RELATED_REFILL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Pharmacy refill request received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "PLACEHOLDER_ACS_PHARMACY_WORKGROUP_ID",
    displayName: "Pharmacy Refill",
    documentLabel: "{{ dateReceived }} Rx Refill - {{ patientInitials }}",
    folder: "Pharmacy",
    folderMapping: "PLACEHOLDER_ACS_PHARMACY_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_REFILL,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PHARMACY_RELATED_CLARIFICATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Pharmacy clarification requested, received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "PLACEHOLDER_ACS_PHARMACY_WORKGROUP_ID",
    displayName: "Pharmacy Clarification",
    documentLabel: "{{ dateReceived }} Rx Clarification - {{ patientInitials }}",
    folder: "Pharmacy",
    folderMapping: "PLACEHOLDER_ACS_PHARMACY_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_CLARIFICATION,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PHARMACY_RELATED_ALERT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Pharmacy Alert",
    documentLabel: "{{ dateReceived }} Pharmacy Alert - Discarded",
    // Payer-initiated drug change / step therapy alert — ACS does not act on these.
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.PHARMACY_RELATED_ALERT,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ORDER_FROM_PHYSICIAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Physician order received {{ dateReceived }}. Please review in Document Management.",
    ],
    assignedTo: "PLACEHOLDER_SHELBY_BROWN_ASSIGNEE_ID",
    displayName: "Order From Physician",
    documentLabel: "{{ dateReceived }} Order - {{ patientInitials }}",
    folder: "Orders",
    folderMapping: "PLACEHOLDER_ACS_ORDERS_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ORDER_FROM_PHYSICIAN,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ORDER_SIGNED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Order Signed",
    documentLabel: "{{ dateReceived }} Signed Order - {{ patientInitials }}",
    folder: "Orders",
    folderMapping: "PLACEHOLDER_ACS_ORDERS_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ORDER_SIGNED,
    group: ConfigSubdomain.ACS,
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Disability form received {{ dateReceived }}. Complete the form, route for provider signature, then notify the patient.",
    ],
    // PLACEHOLDER — swap for Andrea Tucker's real assignee identifier before launch.
    assignedTo: "PLACEHOLDER_ANDREA_TUCKER_ASSIGNEE_ID",
    displayName: "Disability Form",
    documentLabel: "{{ dateReceived }} Disability Form - {{ patientInitials }}",
    folder: "Disability",
    folderMapping: "PLACEHOLDER_ACS_DISABILITY_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.INSURANCE_CARD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Insurance card received {{ dateReceived }}. Please review in Document Management.",
    ],
    // PLACEHOLDER — swap for Rebecca Wood's real assignee identifier before launch.
    assignedTo: "PLACEHOLDER_REBECCA_WOOD_ASSIGNEE_ID",
    displayName: "Insurance Card",
    documentLabel: "{{ dateReceived }} Insurance Card - {{ patientInitials }}",
    folder: "Insurance Card",
    folderMapping: "PLACEHOLDER_ACS_INSURANCE_CARD_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.INSURANCE_CARD,
    group: ConfigSubdomain.ACS,
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "{{ dateReceived }} Junk - {{ patientInitials }}",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.JUNK,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Lab result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    // Nulled by getAcsDocumentManagementInstructions when collectionDate is older than 6 months.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Lab Results",
    documentLabel: "{{ collectionDate }} Labs - {{ patientInitials }}",
    folder: "Labs",
    folderMapping: "PLACEHOLDER_ACS_LABS_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS_QUANTAFLO]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: QuantaFlo result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "QuantaFlo",
    documentLabel: "{{ collectionDate }} QuantaFlo - {{ patientInitials }}",
    folder: "Labs",
    folderMapping: "PLACEHOLDER_ACS_LABS_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_QUANTAFLO,
    group: ConfigSubdomain.ACS,
  },
  [Classification.LAB_RESULTS_PATHOLOGY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Pathology result collected {{ collectionDate }}. Please review in Document Management.",
    ],
    // Always tasked, regardless of date — not part of the freshness-gate map.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Pathology",
    documentLabel: "{{ collectionDate }} Pathology - {{ patientInitials }}",
    folder: "Pathology",
    folderMapping: "PLACEHOLDER_ACS_PATHOLOGY_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.LAB_RESULTS_PATHOLOGY,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_MRI]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: MRI results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "MRI",
    documentLabel: "{{ dateOfEvent }} MRI - {{ patientInitials }}",
    folder: "MRI",
    folderMapping: "PLACEHOLDER_ACS_MRI_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_MRI,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_X_RAY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Chest X-Ray results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Chest X-Ray",
    documentLabel: "{{ dateOfEvent }} Chest X-Ray - {{ patientInitials }}",
    folder: "Chest X-Ray",
    folderMapping: "PLACEHOLDER_ACS_CHEST_X_RAY_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_X_RAY,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_EKG]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: EKG results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "EKG",
    documentLabel: "{{ dateOfEvent }} EKG - {{ patientInitials }}",
    folder: "EKG",
    folderMapping: "PLACEHOLDER_ACS_EKG_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_EKG,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_STRESS_TEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Stress test results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Stress Test",
    documentLabel: "{{ dateOfEvent }} Stress Test - {{ patientInitials }}",
    folder: "Stress Test",
    folderMapping: "PLACEHOLDER_ACS_STRESS_TEST_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_STRESS_TEST,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_NM]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Nuclear medicine results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Nuclear Medicine",
    documentLabel: "{{ dateOfEvent }} Nuclear Medicine - {{ patientInitials }}",
    folder: "Nuclear Medicine",
    folderMapping: "PLACEHOLDER_ACS_NUCLEAR_MEDICINE_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_NM,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_PET]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: PET results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "PET",
    documentLabel: "{{ dateOfEvent }} PET - {{ patientInitials }}",
    folder: "PET",
    folderMapping: "PLACEHOLDER_ACS_PET_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_PET,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_CARDIAC]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Cardiac imaging results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Cardiac MRI studies should be classified as IMAGING_RESULTS_MRI instead (same folder as any
    // other MRI) — this classification is for other cardiac imaging only, per the ACS spec.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Cardiac Imaging",
    documentLabel: "{{ dateOfEvent }} Cardiac Imaging - {{ patientInitials }}",
    folder: "Cardiac Imaging",
    folderMapping: "PLACEHOLDER_ACS_CARDIAC_IMAGING_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_CARDIAC,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Vascular imaging results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Vascular Imaging",
    documentLabel: "{{ dateOfEvent }} Vascular Imaging - {{ patientInitials }}",
    folder: "Vascular Imaging",
    folderMapping: "PLACEHOLDER_ACS_VASCULAR_IMAGING_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_HOLTER_MONITOR]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Holter monitor results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Holter Monitor",
    documentLabel: "{{ dateOfEvent }} Holter Monitor - {{ patientInitials }}",
    folder: "Holter Monitor",
    folderMapping: "PLACEHOLDER_ACS_HOLTER_MONITOR_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_HOLTER_MONITOR,
    group: ConfigSubdomain.ACS,
  },
  [Classification.IMAGING_RESULTS_PULMONARY_FUNCTION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Pulmonary function results from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Pulmonary Function",
    documentLabel: "{{ dateOfEvent }} Pulmonary Function - {{ patientInitials }}",
    folder: "Pulmonary Function",
    folderMapping: "PLACEHOLDER_ACS_PULMONARY_FUNCTION_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.IMAGING_RESULTS_PULMONARY_FUNCTION,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROCEDURE_CARDIAC]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Cath report from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Cath Report",
    documentLabel: "{{ dateOfEvent }} Cath Report - {{ patientInitials }}",
    folder: "Cath Report",
    folderMapping: "PLACEHOLDER_ACS_CATH_REPORT_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROCEDURE_CARDIAC,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROCEDURE_CARDIAC_VEIN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Vascular procedure from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Vascular Procedure",
    documentLabel: "{{ dateOfEvent }} Vascular Procedure - {{ patientInitials }}",
    folder: "Vascular Procedure",
    folderMapping: "PLACEHOLDER_ACS_VASCULAR_PROCEDURE_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROCEDURE_CARDIAC_VEIN,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROCEDURE_DEVICE_INFO]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Device check from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Device Check",
    documentLabel: "{{ dateOfEvent }} Device Check - {{ patientInitials }}",
    folder: "Device Check",
    folderMapping: "PLACEHOLDER_ACS_DEVICE_CHECK_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROCEDURE_DEVICE_INFO,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROCEDURE_SLEEP]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Sleep study from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    // Always tasked, regardless of date — not part of the freshness-gate map.
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Sleep Study",
    documentLabel: "{{ dateOfEvent }} Sleep Study - {{ patientInitials }}",
    folder: "Sleep Study",
    folderMapping: "PLACEHOLDER_ACS_SLEEP_STUDY_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROCEDURE_SLEEP,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: [
      "Task Type: To-Do",
      "Task Details: Hospital discharge summary from {{ dateOfEvent }}. Please review in Document Management.",
    ],
    assignedTo: "Owning Nurse Practitioner",
    displayName: "Hospital Discharge Summary",
    documentLabel: "{{ dateOfEvent }} Hospital Discharge Summary - {{ patientInitials }}",
    folder: "Hospital Records",
    folderMapping: "PLACEHOLDER_ACS_HOSPITAL_RECORDS_DOCUMENT_TYPE_ID",
    inputDate: "Date of Event",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY,
    group: ConfigSubdomain.ACS,
  },
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    // No task, regardless of date — these are ACS's own providers' notes, per the ACS spec.
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Cardiology Hospital Consult",
    // hospitalAbbrev + provider initials (e.g. "STE MJF") is left as a follow-up — needs a
    // dedicated provider-initials helper, not a plain template var.
    documentLabel: "{{ dateReceived }} Cardiology Hospital Consult - {{ patientInitials }}",
    folder: "Cardiology Hospital Consult",
    folderMapping: "PLACEHOLDER_ACS_CARDIOLOGY_HOSPITAL_CONSULT_DOCUMENT_TYPE_ID",
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    priority: "Normal",
    classification: Classification.PROGRESS_NOTE_HOSPITAL_RECORDS,
    group: ConfigSubdomain.ACS,
  },
  [Classification.BLANK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Blank",
    documentLabel: "{{ dateReceived }} Blank",
    folder: null,
    folderMapping: null,
    inputDate: "Date Received",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    priority: "Normal",
    classification: Classification.BLANK,
    group: ConfigSubdomain.ACS,
  },
};
